import { useEffect, useState } from 'react';

const publishedTracks = (participant, kind) =>
  Array.from((kind === 'video' ? participant.videoTracks : participant.audioTracks)?.values() || [])
    .map((publication) => publication.track)
    .filter(Boolean);

// Current video/audio track of a Twilio participant (local or remote).
// - picks up tracks already subscribed/published when mounted,
// - follows trackSubscribed/trackUnsubscribed (remote) and trackPublished/trackUnpublished (local),
// - tracks the enabled flag (remote side toggling its camera/mic).
// Listeners are removed individually: several tiles can render the same participant, and
// removeAllListeners() would also strip Twilio's own and the other tiles' listeners.
export const useParticipantTracks = (participant) => {
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!participant) {
      setVideoTrack(null);
      setAudioTrack(null);
      return undefined;
    }
    // `removed` may still be referenced by its publication while the event is emitted.
    const refresh = (removed) => {
      const exclude = (t) => t !== removed;
      setVideoTrack(publishedTracks(participant, 'video').filter(exclude)[0] || null);
      setAudioTrack(publishedTracks(participant, 'audio').filter(exclude)[0] || null);
    };
    const onAdded = (track) => {
      if (track?.kind === 'video') setVideoTrack(track);
      else if (track?.kind === 'audio') setAudioTrack(track);
      else refresh();
    };
    const onRemoved = (track) => refresh(track);
    const onRefresh = () => refresh();
    const onToggled = () => setVersion((v) => v + 1);

    refresh();
    participant.on('trackSubscribed', onAdded);
    participant.on('trackUnsubscribed', onRemoved);
    participant.on('trackPublished', onRefresh);
    participant.on('trackUnpublished', onRemoved);
    participant.on('trackEnabled', onToggled);
    participant.on('trackDisabled', onToggled);
    participant.on('trackStarted', onToggled);
    return () => {
      participant.off('trackSubscribed', onAdded);
      participant.off('trackUnsubscribed', onRemoved);
      participant.off('trackPublished', onRefresh);
      participant.off('trackUnpublished', onRemoved);
      participant.off('trackEnabled', onToggled);
      participant.off('trackDisabled', onToggled);
      participant.off('trackStarted', onToggled);
    };
  }, [participant]);

  return { videoTrack, audioTrack, isVideoEnabled: videoTrack ? videoTrack.isEnabled !== false : false };
};

// Attach a Twilio track to a media element for the lifetime of the (track, element) pair.
export const useAttachTrack = (track, elementRef) => {
  useEffect(() => {
    const el = elementRef.current;
    if (!track || !el) return undefined;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track, elementRef]);
};
